/**
 * contracts.ts — the byte-for-byte string contracts shared between the trading
 * backend (PRODUCER) and the operator dashboard (CONSUMER).
 *
 * These are the fragile seams flagged in docs/hardening-plan/README.md §1 (A3):
 * the backend emits free-form strings (src/core/bot.ts's /signal detail,
 * src/core/state.ts's halt reasons, the signal-line format) and the UI scrapes
 * them with regexes. A backend reword silently breaks the UI with nothing to
 * catch it. This module is the single typed source of truth; scripts/r88 pins it
 * to real producer strings so drift fails CI, not the operator's feed. When the
 * vanilla dashboard (public/app.js) is ported to React (Phase 6), it imports
 * these instead of re-implementing the regexes.
 *
 * Self-contained (no @/ aliases) so the hermetic test can import it directly.
 */

// ── /signal response detail ─────────────────────────────────────────────────
// Produced by src/core/bot.ts:
//   `<OUTCOME> | <stakeNote> profit=$<n> | next stake=$<n> | daily=<n> | halted=<bool>`
//   stakeNote = `stake=$<n>` (normal) OR `stake req=$<n> actual=$<n>` (override).
export type Outcome = 'win' | 'loss' | 'tie' | 'unknown';

export interface ParsedDetail {
  outcome: Outcome;
  actualStake?: number;
  profitUsd?: number;
}

export function parseDetail(s: string): ParsedDetail {
  const out: ParsedDetail = { outcome: 'unknown' };
  const m = s.match(/^(WIN|LOSS|TIE|UNKNOWN)/i);
  if (m) out.outcome = m[1].toLowerCase() as Outcome;
  // Override rows carry `stake req=$X actual=$Y` — the ACTUAL is the truth.
  const actualM = s.match(/actual=\$(-?[\d.]+)/);
  if (actualM) out.actualStake = Number(actualM[1]);
  // Normal rows carry `stake=$X` (only read it when there was no `actual=`).
  const stakeM = !actualM ? s.match(/stake=\$(-?[\d.]+)/) : null;
  if (stakeM) out.actualStake = Number(stakeM[1]);
  const profitM = s.match(/profit=\$(-?[\d.]+)/);
  if (profitM) out.profitUsd = Number(profitM[1]);
  return out;
}

// ── halt-reason prefixes ────────────────────────────────────────────────────
// Produced by src/core/state.ts. The Stop-Win prefix is load-bearing: the
// dashboard celebration fires on reason.startsWith(HALT_STOP_WIN_PREFIX), and
// state.ts:434 explicitly preserves it EXACTLY.
export const HALT_STOP_WIN_PREFIX = 'Stop-win reached:';

export function isStopWinHalt(haltReason: string | null | undefined): boolean {
  return typeof haltReason === 'string' && haltReason.startsWith(HALT_STOP_WIN_PREFIX);
}

// ── signal-line format ──────────────────────────────────────────────────────
// Mirror of src/signal/parser.ts's grammar (the server stays the source of
// truth on validity; this is for live UI feedback only). Format:
//   <PAIR>[_OTC]-<CALL|PUT>-<TIMEFRAME>/<HH:MM>   e.g. EURUSD_OTC-CALL-M1/01:00
export const SIGNAL_LINE_REGEX =
  /^([A-Z]{3,8})(_OTC)?-(CALL|PUT)-(S30|M1|M3|M5|M15|M30|H1|H4)\/([0-2]?\d):([0-5]\d)$/i;

export function isValidSignalLine(line: string): boolean {
  const m = line.trim().match(SIGNAL_LINE_REGEX);
  if (!m) return false;
  // The regex hour class [0-2]?\d permits up to 29; parser.ts additionally
  // rejects hour > 23 (so 25:00 is invalid). Match the server's REAL validation
  // rather than the looser app.js regex-only check. (Minute is already bounded
  // 00-59 by [0-5]\d.) Group 5 = hour.
  return Number(m[5]) <= 23;
}

// ── local -> UTC time rewrite for batch signal upload ───────────────────────
// The server is UTC-native and trusts the client to pre-convert each `/HH:MM`
// from the operator's LOCAL wall clock to UTC. A time already past today is
// advanced to tomorrow so the server doesn't stale-skip it. `now` is injectable
// (the app.js original wasn't testable); production passes the real clock.
export function convertLocalSignalTimesToUtc(text: string, now: Date = new Date()): string {
  return text
    .split(/\r?\n/)
    .map((line) =>
      line.replace(/\/(\d{1,2}):(\d{2})\s*$/, (full, hh: string, mm: string) => {
        const h = Number(hh);
        const m = Number(mm);
        if (Number.isNaN(h) || Number.isNaN(m) || h > 23 || m > 59) return full;
        const d = new Date(now.getTime());
        d.setHours(h, m, 0, 0); // operator's LOCAL clock today
        if (d.getTime() < now.getTime()) d.setDate(d.getDate() + 1);
        const uh = String(d.getUTCHours()).padStart(2, '0');
        const um = String(d.getUTCMinutes()).padStart(2, '0');
        return `/${uh}:${um}`;
      })
    )
    .join('\n');
}

/** Human-readable LOCAL HH:MM from an absolute UTC ms timestamp. */
export function fmtLocalHHMM(ms: number): string {
  if (!ms || !Number.isFinite(ms)) return '';
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
